/**
 * useInfoModeInteractions — L2 hook for all pointer-interaction logic in PlanRendererInfoMode.
 *
 * Manages: hover highlights (DOM classList), popover state (4 × open + 4 × position),
 * click-to-select, and the effect that syncs the selectedCompartmentId CSS class to the DOM.
 *
 * All enabled flags and classNames are mirrored into refs so that event handlers stay stable
 * (no recreation on panelSettings / capabilities changes).
 *
 * @param {object}          params
 * @param {SVGElement|null} params.svgElement                   — triggers hover-ref reset on change
 * @param {string}          params.viewerVariant                — VIEWER_VARIANT._2D | _3D
 * @param {boolean}         params.compartmentPopoverEnabled
 * @param {boolean}         params.openingsPopoverEnabled
 * @param {boolean}         params.equipmentPopoverEnabled
 * @param {boolean}         params.innerWallsPopoverEnabled
 * @param {object|null}     params.product                      — selection.product
 * @param {boolean}         params.isMobile
 * @param {number|null}     params.selectedCompartmentId
 * @param {function}        params.setSelectedCompartmentId
 * @param {function}        params.setHoveredCompartmentId      — publishes hovered number to ProductContext
 * @param {{ current: HTMLElement|null }} params.containerRef
 * @param {{
 *   compartmentHovered:  string,
 *   compartmentSelected: string,
 *   windowHovered:       string,
 *   equipmentHovered:    string,
 *   partitionHovered:    string,
 * }} params.classNames — CSS Module class names from PlanRendererInfoMode.module.scss
 *
 * @returns {{
 *   handlePointerMove:  function,
 *   handlePointerLeave: function,
 *   handleClick:        function,
 *   popoverState: {
 *     compartment: { data: object|null, position: {x:number,y:number}|null, isOpen: boolean, onClose: function, enabled: boolean },
 *     window:      { data: object|null, position: {x:number,y:number}|null, isOpen: boolean, onClose: function, enabled: boolean },
 *     equipment:   { id: string|null,   position: {x:number,y:number}|null, scale: {x:number,y:number}|null, isOpen: boolean, onClose: function, enabled: boolean },
 *     partition:   {                    position: {x:number,y:number}|null, isOpen: boolean, onClose: function, enabled: boolean },
 *   },
 * }}
 */

import { useEffect, useRef, useCallback, useState } from 'react'

import {
  getCompartmentPath,
  getCompartmentNumber,
  getWindowGroup,
  getWindowIdHex,
  findWindowByIdHex,
  getEquipmentUse,
  getEquipmentUseId,
  getEquipmentUseScale,
  getCaseworkGroup,
  getCaseworkType,
  getPartitionPath,
} from '../utils/svgInfoModeHelpers.js'
import { VIEWER_VARIANT } from '../constants/viewerVariant.js'

export function useInfoModeInteractions({
  svgElement,
  viewerVariant,
  compartmentPopoverEnabled,
  openingsPopoverEnabled,
  equipmentPopoverEnabled,
  innerWallsPopoverEnabled,
  product,
  isMobile,
  selectedCompartmentId,
  setSelectedCompartmentId,
  setHoveredCompartmentId,
  containerRef,
  classNames,
}) {
  // ── Hover-tracking refs — one per interactive element type ────────────────
  const hoveredPathRef      = useRef(null) // path.compartment
  const hoveredWindowRef    = useRef(null) // g.window
  const hoveredEquipmentRef = useRef(null) // use[href] or g.casework.*
  const hoveredPartitionRef = useRef(null) // path.partition

  // ── Stable refs for values read inside event callbacks ────────────────────
  // Allows useCallback deps to stay empty — callbacks are never recreated.
  const viewerVariantRef = useRef(viewerVariant)
  viewerVariantRef.current = viewerVariant

  const classNamesRef = useRef(classNames)
  classNamesRef.current = classNames

  const setHoveredCompartmentIdRef = useRef(setHoveredCompartmentId)
  setHoveredCompartmentIdRef.current = setHoveredCompartmentId

  // ── Popover enabled refs — flag changes do not trigger callback recreation ─
  const compartmentEnabledRef = useRef(compartmentPopoverEnabled)
  compartmentEnabledRef.current = compartmentPopoverEnabled

  const openingsEnabledRef = useRef(openingsPopoverEnabled)
  openingsEnabledRef.current = openingsPopoverEnabled

  const equipmentEnabledRef = useRef(equipmentPopoverEnabled)
  equipmentEnabledRef.current = equipmentPopoverEnabled

  const innerWallsEnabledRef = useRef(innerWallsPopoverEnabled)
  innerWallsEnabledRef.current = innerWallsPopoverEnabled

  // ── Compartment popover state ─────────────────────────────────────────────
  const [compartmentId, setCompartmentId] = useState(null)
  const [compartmentPos, setCompartmentPos] = useState(null)

  const closeCompartmentPopover = useCallback(() => setCompartmentId(null), [])

  const popoverCompartment =
    compartmentPopoverEnabled && compartmentId != null
      ? (product?.compartments?.find((c) => c.number === compartmentId) ?? null)
      : null

  // ── Window popover state ──────────────────────────────────────────────────
  const [windowIdHex, setWindowIdHex] = useState(null)
  const [windowPos, setWindowPos] = useState(null)

  const closeWindowPopover = useCallback(() => setWindowIdHex(null), [])

  const popoverWindow =
    openingsPopoverEnabled && windowIdHex != null
      ? findWindowByIdHex(product, windowIdHex)
      : null

  // ── Equipment popover state ───────────────────────────────────────────────
  const [equipmentId, setEquipmentId] = useState(null)
  const [equipmentScale, setEquipmentScale] = useState(null)
  const [equipmentPos, setEquipmentPos] = useState(null)

  const closeEquipmentPopover = useCallback(() => setEquipmentId(null), [])

  // ── Partition popover state ───────────────────────────────────────────────
  const [partitionOpen, setPartitionOpen] = useState(false)
  const [partitionPos, setPartitionPos] = useState(null)

  const closePartitionPopover = useCallback(() => setPartitionOpen(false), [])

  // ── Reset hover refs when SVG changes (old DOM nodes are gone) ────────────
  useEffect(() => {
    hoveredPathRef.current      = null
    hoveredWindowRef.current    = null
    hoveredEquipmentRef.current = null
    hoveredPartitionRef.current = null
  }, [svgElement])

  // ── Sync selected CSS class → DOM ─────────────────────────────────────────
  // Runs on selectedCompartmentId change and on svgElement change (SVG remounted).
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const cls = classNamesRef.current.compartmentSelected
    const paths = container.querySelectorAll('path.compartment')
    for (const path of paths) {
      const n = getCompartmentNumber(path)
      path.classList.toggle(cls, n === selectedCompartmentId)
    }
  }, [selectedCompartmentId, svgElement, containerRef]) // eslint-disable-line react-hooks/exhaustive-deps -- classNamesRef is stable

  // ── Pointer move ──────────────────────────────────────────────────────────
  //
  // Uses Pointer Events API: one handler for mouse and touch.
  // Touch users tap directly to select — skipping hover avoids a spurious
  // highlight flash before the click fires.
  // Tracks a single ref per element type; at most 2 classList ops per event.

  const handlePointerMove = useCallback((e) => {
    if (e.pointerType === 'touch') return

    const cls   = classNamesRef.current
    const is3D  = viewerVariantRef.current === VIEWER_VARIANT._3D

    // ── Compartment ───────────────────────────────────────────────────────
    const path = getCompartmentPath(e.target)
    if (path !== hoveredPathRef.current) {
      hoveredPathRef.current?.classList.remove(cls.compartmentHovered)
      path?.classList.add(cls.compartmentHovered)
      hoveredPathRef.current = path

      const n = path ? getCompartmentNumber(path) : null
      setHoveredCompartmentIdRef.current?.(n)

      if (compartmentEnabledRef.current) {
        setCompartmentId(n)
        if (n != null) setCompartmentPos({ x: e.clientX, y: e.clientY })
      }
    }

    // Secondary popovers are UI-invisible in 3D (elements hidden by applyRenderTopMutations)
    if (is3D) return

    // ── Window ────────────────────────────────────────────────────────────
    const windowG = getWindowGroup(e.target)
    if (windowG !== hoveredWindowRef.current) {
      hoveredWindowRef.current?.classList.remove(cls.windowHovered)
      windowG?.classList.add(cls.windowHovered)
      hoveredWindowRef.current = windowG

      if (openingsEnabledRef.current) {
        const idHex = windowG ? getWindowIdHex(windowG) : null
        setWindowIdHex(idHex)
        if (idHex != null) setWindowPos({ x: e.clientX, y: e.clientY })
      }
    }

    // ── Equipment (use elements + casework groups) ─────────────────────────
    const useEl    = getEquipmentUse(e.target)
    const casework = useEl ? null : getCaseworkGroup(e.target)
    const equipEl  = useEl ?? casework ?? null

    if (equipEl !== hoveredEquipmentRef.current) {
      hoveredEquipmentRef.current?.classList.remove(cls.equipmentHovered)
      equipEl?.classList.add(cls.equipmentHovered)
      hoveredEquipmentRef.current = equipEl

      if (equipmentEnabledRef.current) {
        const id = equipEl
          ? (useEl ? getEquipmentUseId(useEl) : getCaseworkType(casework))
          : null
        setEquipmentId(id)
        if (id != null) {
          setEquipmentPos({ x: e.clientX, y: e.clientY })
          setEquipmentScale(getEquipmentUseScale(useEl))
        }
      }
    }

    // ── Partition ─────────────────────────────────────────────────────────
    const partPath = getPartitionPath(e.target)
    if (partPath !== hoveredPartitionRef.current) {
      hoveredPartitionRef.current?.classList.remove(cls.partitionHovered)
      partPath?.classList.add(cls.partitionHovered)
      hoveredPartitionRef.current = partPath

      if (innerWallsEnabledRef.current) {
        setPartitionOpen(partPath != null)
        if (partPath != null) setPartitionPos({ x: e.clientX, y: e.clientY })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- all mutable values accessed via stable refs

  // ── Pointer leave ─────────────────────────────────────────────────────────

  const handlePointerLeave = useCallback((e) => {
    if (e.pointerType === 'touch') return

    const cls = classNamesRef.current

    hoveredPathRef.current?.classList.remove(cls.compartmentHovered)
    hoveredPathRef.current = null
    setHoveredCompartmentIdRef.current?.(null)
    if (compartmentEnabledRef.current) setCompartmentId(null)

    hoveredWindowRef.current?.classList.remove(cls.windowHovered)
    hoveredWindowRef.current = null
    if (openingsEnabledRef.current) setWindowIdHex(null)

    hoveredEquipmentRef.current?.classList.remove(cls.equipmentHovered)
    hoveredEquipmentRef.current = null
    if (equipmentEnabledRef.current) setEquipmentId(null)

    hoveredPartitionRef.current?.classList.remove(cls.partitionHovered)
    hoveredPartitionRef.current = null
    if (innerWallsEnabledRef.current) setPartitionOpen(false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- all mutable values accessed via stable refs

  // ── Click / select ────────────────────────────────────────────────────────

  const handleClick = useCallback((e) => {
    if (isMobile) {
      const x = Number.isFinite(e.clientX) ? e.clientX : 0
      const y = Number.isFinite(e.clientY) ? e.clientY : 0

      // Keep only one popover open after each tap.
      setCompartmentId(null)
      setWindowIdHex(null)
      setEquipmentId(null)
      setPartitionOpen(false)

      const useEl = getEquipmentUse(e.target)
      const casework = useEl ? null : getCaseworkGroup(e.target)
      const equipEl = useEl ?? casework ?? null
      if (equipmentEnabledRef.current && equipEl) {
        const id = useEl ? getEquipmentUseId(useEl) : getCaseworkType(casework)
        setEquipmentId(id)
        if (id != null) {
          setEquipmentPos({ x, y })
          setEquipmentScale(getEquipmentUseScale(useEl))
        }
        return
      }

      const windowG = getWindowGroup(e.target)
      if (openingsEnabledRef.current && windowG) {
        const idHex = getWindowIdHex(windowG)
        setWindowIdHex(idHex)
        if (idHex != null) setWindowPos({ x, y })
        return
      }

      const partPath = getPartitionPath(e.target)
      if (innerWallsEnabledRef.current && partPath) {
        setPartitionOpen(true)
        setPartitionPos({ x, y })
        return
      }

      if (compartmentEnabledRef.current) {
        const path = getCompartmentPath(e.target)
        const n = getCompartmentNumber(path)
        if (n != null) {
          setCompartmentId(n)
          setCompartmentPos({ x, y })
        }
      }
    }

    const path = getCompartmentPath(e.target)
    const n    = getCompartmentNumber(path)
    if (n == null) return
    // Toggle: clicking the same compartment again deselects it
    setSelectedCompartmentId((prev) => (prev === n ? null : n))
  }, [isMobile, setSelectedCompartmentId])

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    handlePointerMove,
    handlePointerLeave,
    handleClick,
    popoverState: {
      compartment: {
        data:     popoverCompartment,
        position: compartmentPos,
        isOpen:   compartmentId != null,
        onClose:  closeCompartmentPopover,
        enabled:  compartmentPopoverEnabled,
      },
      window: {
        data:     popoverWindow,
        position: windowPos,
        isOpen:   windowIdHex != null,
        onClose:  closeWindowPopover,
        enabled:  openingsPopoverEnabled,
      },
      equipment: {
        id:       equipmentId,
        position: equipmentPos,
        scale:    equipmentScale,
        isOpen:   equipmentId != null,
        onClose:  closeEquipmentPopover,
        enabled:  equipmentPopoverEnabled,
      },
      partition: {
        position: partitionPos,
        isOpen:   partitionOpen,
        onClose:  closePartitionPopover,
        enabled:  innerWallsPopoverEnabled,
      },
    },
  }
}
